<template>
    <DefaultHeader />

    <main class="container position-share-page">
        <section v-if="baliseInfo.id" class="card shadow bg-white mb-3 position-share-vehicle-card">
            <div class="card-body">
                <VehicleNameDisplay :balise="baliseInfo" :showStatuses="true" :linkToMap="false" />
            </div>
        </section>

        <section class="card shadow bg-white mb-4 position-share-management-card">
            <div class="card-header position-share-page-header">
                <div class="position-share-page-heading">
                    <h1 class="position-share-page-title">
                        Liens de partage de position
                    </h1>

                    <div class="position-share-page-subtitle">
                        Gérez les accès temporaires à la position du véhicule.
                    </div>
                </div>

                <div class="position-share-create">
                    <select v-model.number="durationMinutes" class="form-select form-select-sm position-share-duration"
                        :disabled="isCreating" aria-label="Durée du nouveau lien">
                        <option :value="30">30 min</option>
                        <option :value="60">1 h</option>
                        <option :value="120">2 h</option>
                        <option :value="180">3 h</option>
                        <option :value="360">6 h</option>
                        <option :value="720">12 h</option>
                        <option :value="1440">24 h</option>
                    </select>

                    <button type="button" class="btn btn-sm btn-primary position-share-create-button"
                        :disabled="isCreating || !baliseInfo.id" @click="createLink">
                        <span v-if="isCreating" class="spinner-border spinner-border-sm" role="status"
                            aria-hidden="true" />

                        <svg v-else viewBox="0 0 24 24" aria-hidden="true">
                            <path d="M12 5v14" />
                            <path d="M5 12h14" />
                        </svg>

                        <span>
                            Créer
                        </span>
                    </button>
                </div>
            </div>

            <div v-if="message" :class="[
                'position-share-message',
                messageType === 'error'
                    ? 'position-share-message-error'
                    : 'position-share-message-success'
            ]">
                {{ message }}
            </div>
        </section>

        <div v-if="isLoading" class="card shadow-sm bg-white position-share-loading">
            <div class="card-body">
                <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />

                Chargement des liens...
            </div>
        </div>

        <template v-else>
            <section class="position-share-section">
                <div class="position-share-section-header">
                    <div>
                        <h2 class="position-share-section-title">
                            Liens actifs
                        </h2>

                        <div class="position-share-section-subtitle">
                            {{ activeLinks.length }}
                            {{
                                activeLinks.length > 1
                                    ? 'liens disponibles'
                                    : 'lien disponible'
                            }}
                        </div>
                    </div>

                    <span class="badge rounded-pill bg-success">
                        {{ activeLinks.length }}
                    </span>
                </div>

                <div v-if="activeLinks.length === 0" class="card shadow-sm bg-white position-share-empty">
                    <div class="card-body">
                        <svg viewBox="0 0 24 24" aria-hidden="true" class="position-share-empty-icon">
                            <circle cx="12" cy="12" r="9" />
                            <path d="M12 7v5l3 2" />
                        </svg>

                        <div>
                            <div class="position-share-empty-title">
                                Aucun lien actif
                            </div>

                            <div class="position-share-empty-text">
                                Créez un lien pour partager temporairement la position.
                            </div>
                        </div>
                    </div>
                </div>

                <div v-else class="position-share-list">
                    <article v-for="shareLink in activeLinks" :key="shareLink.id"
                        class="card shadow-sm bg-white position-share-row position-share-row-active">
                        <div class="card-body">
                            <div class="position-share-row-layout">
                                <div class="position-share-row-main">
                                    <div class="position-share-identity">
                                        <span class="position-share-status-dot position-share-status-dot-active" />

                                        <span class="position-share-link-title">
                                            Lien #{{ shareLink.id }}
                                        </span>

                                        <span class="position-share-separator">
                                            ·
                                        </span>

                                        <span class="position-share-remaining">
                                            {{ getRemainingTime(shareLink.expiresAt) }}
                                        </span>
                                    </div>

                                    <div class="position-share-metadata">
                                        <span class="position-share-metadata-item">
                                            <span class="position-share-metadata-label">
                                                Créé
                                            </span>

                                            <span>
                                                {{ formatDate(shareLink.createdAt) }}
                                            </span>
                                        </span>

                                        <span class="position-share-metadata-separator">
                                            ·
                                        </span>

                                        <span class="position-share-metadata-item">
                                            <span class="position-share-metadata-label">
                                                Par
                                            </span>

                                            <span>
                                                {{ getUserDisplayName(shareLink.createdByUser) }}
                                            </span>
                                        </span>

                                        <span class="position-share-metadata-separator">
                                            ·
                                        </span>

                                        <span class="position-share-metadata-item">
                                            <span class="position-share-metadata-label">
                                                Expire
                                            </span>

                                            <span>
                                                {{ formatDate(shareLink.expiresAt) }}
                                            </span>
                                        </span>
                                    </div>
                                </div>

                                <div class="position-share-actions">
                                    <button type="button" class="btn btn-sm position-share-action" :class="{
                                        'position-share-action-copied':
                                            copiedLinkId === shareLink.id
                                    }" :disabled="!shareLink.token" :title="copiedLinkId === shareLink.id
                                            ? 'Lien copié'
                                            : 'Copier le lien'" @click="copyLink(shareLink)">
                                        <svg v-if="copiedLinkId !== shareLink.id" viewBox="0 0 24 24"
                                            aria-hidden="true">
                                            <rect x="9" y="9" width="11" height="11" rx="2" />

                                            <path d="M15 9V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h3" />
                                        </svg>

                                        <svg v-else viewBox="0 0 24 24" aria-hidden="true">
                                            <path d="m5 12 4 4L19 6" />
                                        </svg>

                                        <span>
                                            {{
                                                copiedLinkId === shareLink.id
                                                    ? 'Copié'
                                                    : 'Copier'
                                            }}
                                        </span>
                                    </button>

                                    <div class="position-share-extend">
                                        <select v-model.number="extendDurations[shareLink.id]"
                                            class="form-select form-select-sm position-share-extend-duration"
                                            :disabled="extendingLinkId === shareLink.id"
                                            :aria-label="`Durée à ajouter au lien ${shareLink.id}`">
                                            <option :value="30">+ 30 min</option>
                                            <option :value="60">+ 1 h</option>
                                            <option :value="120">+ 2 h</option>
                                            <option :value="180">+ 3 h</option>
                                            <option :value="360">+ 6 h</option>
                                            <option :value="720">+ 12 h</option>
                                            <option :value="1440">+ 24 h</option>
                                        </select>

                                        <button type="button"
                                            class="btn btn-sm position-share-action position-share-action-primary"
                                            :disabled="extendingLinkId === shareLink.id" title="Ajouter du temps"
                                            @click="extendLink(shareLink)">
                                            <span v-if="extendingLinkId === shareLink.id"
                                                class="spinner-border spinner-border-sm" role="status"
                                                aria-hidden="true" />

                                            <svg v-else viewBox="0 0 24 24" aria-hidden="true">
                                                <circle cx="12" cy="12" r="9" />
                                                <path d="M12 7v5l3 2" />
                                                <path d="M19 5v4h-4" />
                                            </svg>

                                            <span>
                                                Ajouter
                                            </span>
                                        </button>
                                    </div>

                                    <button type="button"
                                        class="btn btn-sm position-share-action position-share-action-danger"
                                        :disabled="revokingLinkId === shareLink.id" title="Révoquer le lien"
                                        @click="revokeLink(shareLink)">
                                        <span v-if="revokingLinkId === shareLink.id"
                                            class="spinner-border spinner-border-sm" role="status" aria-hidden="true" />

                                        <svg v-else viewBox="0 0 24 24" aria-hidden="true">
                                            <path d="M3 6h18" />
                                            <path d="M8 6V4h8v2" />
                                            <path d="M19 6l-1 14H6L5 6" />
                                            <path d="M10 11v5" />
                                            <path d="M14 11v5" />
                                        </svg>

                                        <span>
                                            Révoquer
                                        </span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </article>
                </div>
            </section>

            <section class="position-share-section position-share-section-old">
                <div class="position-share-section-header">
                    <div>
                        <h2 class="position-share-section-title">
                            Anciens liens
                        </h2>

                        <div class="position-share-section-subtitle">
                            Liens expirés ou révoqués, classés par date de création
                        </div>
                    </div>

                    <span class="badge rounded-pill bg-secondary">
                        {{ oldLinks.length }}
                    </span>
                </div>

                <div v-if="oldLinks.length === 0" class="card shadow-sm bg-white position-share-empty">
                    <div class="card-body">
                        <svg viewBox="0 0 24 24" aria-hidden="true" class="position-share-empty-icon">
                            <path d="M4 7h16" />
                            <path d="M5 7l1 13h12l1-13" />
                            <path d="M9 11h6" />
                            <path d="M8 4h8l1 3H7z" />
                        </svg>

                        <div>
                            <div class="position-share-empty-title">
                                Aucun ancien lien
                            </div>

                            <div class="position-share-empty-text">
                                Les liens expirés ou révoqués apparaîtront ici.
                            </div>
                        </div>
                    </div>
                </div>

                <div v-else class="position-share-list">
                    <article v-for="shareLink in oldLinks" :key="shareLink.id"
                        class="card shadow-sm bg-white position-share-row position-share-row-old">
                        <div class="card-body">
                            <div class="position-share-row-layout position-share-row-layout-old">
                                <div class="position-share-row-main">
                                    <div class="position-share-old-line">
                                        <div class="position-share-identity">
                                            <span :class="[
                                                'position-share-status-dot',
                                                shareLink.isRevoked
                                                    ? 'position-share-status-dot-revoked'
                                                    : 'position-share-status-dot-expired'
                                            ]" />

                                            <span class="position-share-link-title">
                                                Lien #{{ shareLink.id }}
                                            </span>

                                            <span :class="[
                                                'position-share-status-badge',
                                                shareLink.isRevoked
                                                    ? 'position-share-status-badge-revoked'
                                                    : 'position-share-status-badge-expired'
                                            ]">
                                                {{
                                                    shareLink.isRevoked
                                                        ? 'Révoqué'
                                                        : 'Expiré'
                                                }}
                                            </span>
                                        </div>

                                        <div class="position-share-metadata position-share-metadata-old">
                                            <span class="position-share-metadata-item">
                                                <span class="position-share-metadata-label">
                                                    Créé
                                                </span>

                                                <span>
                                                    {{ formatDate(shareLink.createdAt) }}
                                                </span>
                                            </span>

                                            <span class="position-share-metadata-separator">
                                                ·
                                            </span>

                                            <span class="position-share-metadata-item">
                                                <span class="position-share-metadata-label">
                                                    Par
                                                </span>

                                                <span>
                                                    {{ getUserDisplayName(shareLink.createdByUser) }}
                                                </span>
                                            </span>

                                            <template v-if="shareLink.revokedAt">
                                                <span class="position-share-metadata-separator">
                                                    ·
                                                </span>

                                                <span class="position-share-metadata-item">
                                                    <span class="position-share-metadata-label">
                                                        Révoqué
                                                    </span>

                                                    <span>
                                                        {{ formatDate(shareLink.revokedAt) }}
                                                    </span>
                                                </span>

                                                <span class="position-share-metadata-separator">
                                                    ·
                                                </span>

                                                <span class="position-share-metadata-item">
                                                    <span class="position-share-metadata-label">
                                                        Par
                                                    </span>

                                                    <span>
                                                        {{ getUserDisplayName(shareLink.revokedByUser) }}
                                                    </span>
                                                </span>
                                            </template>

                                            <template v-else>
                                                <span class="position-share-metadata-separator">
                                                    ·
                                                </span>

                                                <span class="position-share-metadata-item">
                                                    <span class="position-share-metadata-label">
                                                        Expiré
                                                    </span>

                                                    <span>
                                                        {{ formatDate(shareLink.expiresAt) }}
                                                    </span>
                                                </span>
                                            </template>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </article>
                </div>
            </section>
        </template>
    </main>

    <DefaultFooter />
</template>

<script>
import DefaultHeader from '@/components/DefaultHeader.vue'
import DefaultFooter from '@/components/DefaultFooter.vue'
import VehicleNameDisplay from '@/components/display/VehicleNameDisplay.vue'
import BaliseRepository from '@/repositories/balise.repo.js'
import PositionShareService from '@/services/positionShare.service'

const PUBLIC_POSITION_SHARE_BASE_URL =
    'https://espace.argos-geoloc.com/positionshare';

export default {
    name: 'PositionShareLinksPage',

    components: {
        DefaultHeader,
        DefaultFooter,
        VehicleNameDisplay
    },

    data() {
        return {
            baliseInfo: {},
            links: [],

            durationMinutes: 60,
            extendDurations: {},

            isLoading: true,
            isCreating: false,
            revokingLinkId: null,
            extendingLinkId: null,
            copiedLinkId: null,

            message: '',
            messageType: 'success',

            now: Date.now(),
            remainingTimeInterval: null,
            copiedMessageTimeout: null,
            messageTimeout: null
        };
    },

    computed: {
        baliseId() {
            return Number(this.$route.params.id);
        },

        activeLinks() {
            return this.links.filter(shareLink => {
                return (
                    !shareLink.revokedAt &&
                    new Date(shareLink.expiresAt).getTime() > this.now
                );
            });
        },

        oldLinks() {
            return this.links.filter(shareLink => {
                return (
                    shareLink.revokedAt ||
                    new Date(shareLink.expiresAt).getTime() <= this.now
                );
            });
        }
    },

    mounted() {
        this.loadPage();

        this.remainingTimeInterval = setInterval(() => {
            this.now = Date.now();
        }, 1000);
    },

    beforeUnmount() {
        if (this.remainingTimeInterval) {
            clearInterval(this.remainingTimeInterval);
        }

        if (this.copiedMessageTimeout) {
            clearTimeout(this.copiedMessageTimeout);
        }

        if (this.messageTimeout) {
            clearTimeout(this.messageTimeout);
        }
    },

    methods: {
        async loadPage() {
            if (
                !Number.isInteger(this.baliseId) ||
                this.baliseId <= 0
            ) {
                this.isLoading = false;

                this.showMessage(
                    'Identifiant de balise invalide.',
                    'error'
                );

                return;
            }

            this.isLoading = true;

            try {
                const [balise, linksResponse] =
                    await Promise.all([
                        BaliseRepository.getBalise(
                            this.baliseId
                        ),
                        PositionShareService
                            .getPositionShareLinks(
                                this.baliseId
                            )
                    ]);

                this.baliseInfo = balise;

                this.links = Array.isArray(
                    linksResponse.data
                )
                    ? linksResponse.data
                    : [];

                this.initializeExtendDurations();
            } catch (error) {
                console.error(
                    'Erreur chargement de la page des liens de partage :',
                    error
                );

                this.showMessage(
                    error.response?.data?.message ||
                    'Impossible de charger les liens de partage.',
                    'error'
                );
            } finally {
                this.isLoading = false;
            }
        },

        async loadLinks() {
            const response =
                await PositionShareService
                    .getPositionShareLinks(
                        this.baliseId
                    );

            this.links = Array.isArray(response.data)
                ? response.data
                : [];

            this.initializeExtendDurations();
        },

        initializeExtendDurations() {
            const durations = {
                ...this.extendDurations
            };

            this.links.forEach(shareLink => {
                if (durations[shareLink.id] == null) {
                    durations[shareLink.id] = 60;
                }
            });

            this.extendDurations = durations;
        },

        async createLink() {
            if (
                this.isCreating ||
                !this.baliseInfo.id
            ) {
                return;
            }

            this.isCreating = true;

            try {
                const response =
                    await PositionShareService
                        .createPositionShareLink(
                            this.baliseInfo.id,
                            this.durationMinutes
                        );

                const createdLink = response.data;

                await this.loadLinks();

                const publicUrl =
                    this.getPublicShareUrl(
                        createdLink
                    );

                if (!publicUrl) {
                    this.showMessage(
                        'Lien créé.',
                        'success'
                    );

                    return;
                }

                try {
                    await this.copyText(publicUrl);

                    this.copiedLinkId =
                        createdLink.id;

                    this.resetCopiedState();

                    this.showMessage(
                        'Lien créé et copié.',
                        'success'
                    );
                } catch {
                    this.showMessage(
                        'Lien créé.',
                        'success'
                    );
                }
            } catch (error) {
                console.error(
                    'Erreur création du lien de partage :',
                    error
                );

                this.showMessage(
                    error.response?.data?.message ||
                    'Impossible de créer le lien.',
                    'error'
                );
            } finally {
                this.isCreating = false;
            }
        },

        async extendLink(shareLink) {
            if (
                this.extendingLinkId !== null ||
                !shareLink?.id
            ) {
                return;
            }

            const durationMinutes =
                Number(
                    this.extendDurations[
                    shareLink.id
                    ]
                );

            if (
                !Number.isInteger(durationMinutes) ||
                durationMinutes <= 0
            ) {
                this.showMessage(
                    'Durée de prolongation invalide.',
                    'error'
                );

                return;
            }

            this.extendingLinkId =
                shareLink.id;

            try {
                await PositionShareService
                    .extendPositionShareLink(
                        this.baliseInfo.id,
                        shareLink.id,
                        durationMinutes
                    );

                await this.loadLinks();

                this.showMessage(
                    'Le lien a été prolongé.',
                    'success'
                );
            } catch (error) {
                console.error(
                    'Erreur prolongation du lien de partage :',
                    error
                );

                this.showMessage(
                    error.response?.data?.message ||
                    'Impossible de prolonger le lien.',
                    'error'
                );
            } finally {
                this.extendingLinkId = null;
            }
        },

        async revokeLink(shareLink) {
            if (
                this.revokingLinkId !== null ||
                !shareLink?.id
            ) {
                return;
            }

            const confirmed = window.confirm(
                'Révoquer définitivement ce lien de partage ?'
            );

            if (!confirmed) {
                return;
            }

            this.revokingLinkId =
                shareLink.id;

            try {
                await PositionShareService
                    .revokePositionShareLink(
                        this.baliseInfo.id,
                        shareLink.id
                    );

                await this.loadLinks();

                this.showMessage(
                    'Le lien a été révoqué.',
                    'success'
                );
            } catch (error) {
                console.error(
                    'Erreur révocation du lien de partage :',
                    error
                );

                this.showMessage(
                    error.response?.data?.message ||
                    'Impossible de révoquer le lien.',
                    'error'
                );
            } finally {
                this.revokingLinkId = null;
            }
        },

        async copyLink(shareLink) {
            const publicUrl =
                this.getPublicShareUrl(
                    shareLink
                );

            if (!publicUrl) {
                return;
            }

            try {
                await this.copyText(publicUrl);

                this.copiedLinkId =
                    shareLink.id;

                this.resetCopiedState();

                this.showMessage(
                    'Lien copié.',
                    'success'
                );
            } catch (error) {
                console.error(
                    'Erreur copie du lien :',
                    error
                );

                this.showMessage(
                    'La copie du lien a échoué.',
                    'error'
                );
            }
        },

        getPublicShareUrl(shareLink) {
            if (!shareLink?.token) {
                return null;
            }

            return `${PUBLIC_POSITION_SHARE_BASE_URL}/${encodeURIComponent(
                shareLink.token
            )}`;
        },

        copyText(text) {
            if (
                navigator.clipboard &&
                window.isSecureContext
            ) {
                return navigator.clipboard
                    .writeText(text);
            }

            return new Promise(
                (resolve, reject) => {
                    const textarea =
                        document.createElement(
                            'textarea'
                        );

                    textarea.value = text;
                    textarea.setAttribute(
                        'readonly',
                        ''
                    );

                    textarea.style.position =
                        'fixed';

                    textarea.style.opacity =
                        '0';

                    document.body.appendChild(
                        textarea
                    );

                    textarea.select();

                    textarea.setSelectionRange(
                        0,
                        textarea.value.length
                    );

                    try {
                        const copied =
                            document.execCommand(
                                'copy'
                            );

                        document.body.removeChild(
                            textarea
                        );

                        if (!copied) {
                            reject(
                                new Error(
                                    'Copie refusée'
                                )
                            );

                            return;
                        }

                        resolve();
                    } catch (error) {
                        document.body.removeChild(
                            textarea
                        );

                        reject(error);
                    }
                }
            );
        },

        getUserDisplayName(user) {
            if (!user) {
                return 'Non renseigné';
            }

            const firstName =
                user.firstname ??
                user.firstName ??
                user.prenom ??
                '';

            const lastName =
                user.lastname ??
                user.lastName ??
                user.nom ??
                '';

            const fullName =
                `${firstName} ${lastName}`.trim();

            return (
                fullName ||
                user.name ||
                user.username ||
                user.email ||
                `Utilisateur #${user.id}`
            );
        },

        formatDate(date) {
            const parsedDate =
                new Date(date);

            if (
                Number.isNaN(
                    parsedDate.getTime()
                )
            ) {
                return 'Date inconnue';
            }

            return new Intl.DateTimeFormat(
                'fr-FR',
                {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                }
            ).format(parsedDate);
        },

        getRemainingTime(expiresAt) {
            const expirationTime =
                new Date(expiresAt).getTime();

            const remainingMilliseconds =
                expirationTime - this.now;

            if (
                Number.isNaN(expirationTime) ||
                remainingMilliseconds <= 0
            ) {
                return 'Expiré';
            }

            const totalMinutes =
                Math.floor(
                    remainingMilliseconds / 60000
                );

            const days =
                Math.floor(
                    totalMinutes / 1440
                );

            const hours =
                Math.floor(
                    (totalMinutes % 1440) / 60
                );

            const minutes =
                totalMinutes % 60;

            if (days > 0) {
                return `${days} j ${hours} h restantes`;
            }

            if (hours > 0) {
                return `${hours} h ${minutes} min restantes`;
            }

            if (minutes > 0) {
                return `${minutes} min restantes`;
            }

            return 'Moins d’une minute';
        },

        showMessage(message, type) {
            this.message = message;
            this.messageType = type;

            if (this.messageTimeout) {
                clearTimeout(
                    this.messageTimeout
                );
            }

            this.messageTimeout =
                setTimeout(() => {
                    this.message = '';
                }, 3000);
        },

        resetCopiedState() {
            if (
                this.copiedMessageTimeout
            ) {
                clearTimeout(
                    this.copiedMessageTimeout
                );
            }

            this.copiedMessageTimeout =
                setTimeout(() => {
                    this.copiedLinkId = null;
                }, 1800);
        }
    }
}
</script>

<style scoped>
.position-share-page {
    min-height: calc(100vh - 150px);
    padding-top: 1.25rem;
    padding-bottom: 2rem;
}

.position-share-vehicle-card,
.position-share-management-card,
.position-share-row,
.position-share-empty,
.position-share-loading {
    max-width: 100%;
    overflow: hidden;
    border: 1px solid rgba(0, 0, 0, 0.08);
}

.position-share-vehicle-card .card-body {
    padding: 1rem 1.25rem;
}

.position-share-page-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    min-width: 0;
    padding: 0.85rem 1rem;
    background: #fff;
}

.position-share-page-heading {
    min-width: 0;
}

.position-share-page-title {
    overflow: hidden;
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
    line-height: 1.25;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.position-share-page-subtitle {
    overflow: hidden;
    margin-top: 0.15rem;
    color: #6c757d;
    font-size: 0.75rem;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.position-share-create {
    display: flex;
    align-items: center;
    flex: 0 0 auto;
    gap: 0.4rem;
}

.position-share-duration {
    width: 82px;
    min-width: 82px;
    font-size: 0.75rem;
}

.position-share-create-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.35rem;
    height: 31px;
    padding: 0 0.6rem;
    font-size: 0.7rem;
    white-space: nowrap;
}

.position-share-create-button svg {
    width: 15px;
    height: 15px;
    flex-shrink: 0;
    fill: none;
    stroke: currentColor;
    stroke-width: 2;
    stroke-linecap: round;
}

.position-share-message {
    margin: 0.65rem 0.85rem 0.85rem;
    padding: 0.5rem 0.7rem;
    font-size: 0.76rem;
    border-radius: 0.375rem;
}

.position-share-message-success {
    color: #0f5132;
    background: #d1e7dd;
}

.position-share-message-error {
    color: #842029;
    background: #f8d7da;
}

.position-share-loading .card-body {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 80px;
    color: #6c757d;
    font-size: 0.8rem;
}

.position-share-section {
    margin-bottom: 1.5rem;
}

.position-share-section-old {
    margin-top: 1.75rem;
}

.position-share-section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 0.55rem;
    padding: 0 0.15rem;
}

.position-share-section-title {
    margin: 0;
    font-size: 0.95rem;
    font-weight: 600;
}

.position-share-section-subtitle {
    margin-top: 0.1rem;
    color: #6c757d;
    font-size: 0.7rem;
}

.position-share-list {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
}

.position-share-row {
    transition:
        box-shadow 0.15s ease,
        background-color 0.15s ease;
}

.position-share-row-active {
    border-left: 3px solid #198754;
}

.position-share-row-active:hover {
    background: #fcfdfd;
}

.position-share-row-old {
    border-left: 3px solid #adb5bd;
    background: #fbfbfc !important;
}

.position-share-row .card-body {
    padding: 0.65rem 0.75rem;
}

.position-share-row-layout {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.85rem;
    min-width: 0;
}

.position-share-row-layout-old {
    min-height: 30px;
}

.position-share-row-main {
    min-width: 0;
    flex: 1 1 auto;
}

.position-share-identity {
    display: flex;
    align-items: center;
    min-width: 0;
    white-space: nowrap;
}

.position-share-status-dot {
    width: 7px;
    height: 7px;
    margin-right: 0.5rem;
    flex: 0 0 7px;
    border-radius: 50%;
}

.position-share-status-dot-active {
    background: #198754;
    box-shadow: 0 0 0 3px rgba(25, 135, 84, 0.12);
}

.position-share-status-dot-expired {
    background: #6c757d;
}

.position-share-status-dot-revoked {
    background: #dc3545;
}

.position-share-link-title {
    flex: 0 0 auto;
    font-size: 0.8rem;
    font-weight: 600;
}

.position-share-separator,
.position-share-metadata-separator {
    flex: 0 0 auto;
    margin: 0 0.35rem;
    color: #adb5bd;
}

.position-share-remaining {
    overflow: hidden;
    color: #198754;
    font-size: 0.74rem;
    font-weight: 500;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.position-share-metadata {
    display: flex;
    align-items: center;
    min-width: 0;
    margin-top: 0.25rem;
    overflow: hidden;
    color: #5f6875;
    font-size: 0.69rem;
    white-space: nowrap;
}

.position-share-metadata-old {
    margin-top: 0;
    margin-left: 1rem;
}

.position-share-metadata-item {
    overflow: hidden;
    min-width: 0;
    text-overflow: ellipsis;
}

.position-share-metadata-label {
    margin-right: 0.2rem;
    color: #8a929b;
}

.position-share-old-line {
    display: flex;
    align-items: center;
    min-width: 0;
}

.position-share-status-badge {
    display: inline-flex;
    align-items: center;
    margin-left: 0.45rem;
    padding: 0.12rem 0.4rem;
    flex: 0 0 auto;
    font-size: 0.62rem;
    font-weight: 500;
    line-height: 1.2;
    border-radius: 999px;
}

.position-share-status-badge-expired {
    color: #495057;
    background: #e9ecef;
}

.position-share-status-badge-revoked {
    color: #842029;
    background: #f8d7da;
}

.position-share-actions {
    display: flex;
    align-items: center;
    flex: 0 0 auto;
    gap: 0.25rem;
}

.position-share-action {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.3rem;
    height: 29px;
    padding: 0 0.4rem;
    color: #6c757d;
    background: transparent;
    border: 0;
    font-size: 0.68rem;
    white-space: nowrap;
}

.position-share-action:hover:not(:disabled) {
    color: #212529;
    background: #e9ecef;
}

.position-share-action-primary:hover:not(:disabled) {
    color: #0d6efd;
    background: rgba(13, 110, 253, 0.08);
}

.position-share-action-danger:hover:not(:disabled) {
    color: #dc3545;
    background: rgba(220, 53, 69, 0.08);
}

.position-share-action-copied {
    color: #198754;
    background: rgba(25, 135, 84, 0.08);
}

.position-share-action:disabled {
    opacity: 0.45;
}

.position-share-action svg {
    width: 15px;
    height: 15px;
    flex: 0 0 15px;
    fill: none;
    stroke: currentColor;
    stroke-width: 1.8;
    stroke-linecap: round;
    stroke-linejoin: round;
}

.position-share-action .spinner-border {
    width: 0.75rem;
    height: 0.75rem;
    border-width: 0.12em;
}

.position-share-extend {
    display: flex;
    align-items: center;
    gap: 0.2rem;
}

.position-share-extend-duration {
    width: 83px;
    min-width: 83px;
    font-size: 0.7rem;
}

.position-share-empty .card-body {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    min-height: 70px;
    padding: 0.8rem 1rem;
    color: #6c757d;
}

.position-share-empty-icon {
    width: 20px;
    height: 20px;
    flex: 0 0 20px;
    fill: none;
    stroke: #adb5bd;
    stroke-width: 1.7;
    stroke-linecap: round;
    stroke-linejoin: round;
}

.position-share-empty-title {
    color: #495057;
    font-size: 0.8rem;
    font-weight: 600;
}

.position-share-empty-text {
    margin-top: 0.1rem;
    font-size: 0.7rem;
}

@media (max-width: 1199.98px) {
    .position-share-row-layout {
        align-items: stretch;
        flex-direction: column;
        gap: 0.5rem;
    }

    .position-share-actions {
        justify-content: flex-end;
        padding-top: 0.45rem;
        border-top: 1px solid #eef0f2;
    }
}

@media (max-width: 767.98px) {
    .position-share-page-header {
        align-items: stretch;
        flex-direction: column;
    }

    .position-share-page-title,
    .position-share-page-subtitle {
        white-space: normal;
    }

    .position-share-create {
        justify-content: flex-end;
    }

    .position-share-old-line {
        align-items: flex-start;
        flex-direction: column;
        gap: 0.35rem;
    }

    .position-share-metadata-old {
        margin-top: 0.1rem;
        margin-left: 1.2rem;
    }

    .position-share-actions {
        align-items: stretch;
        flex-wrap: wrap;
    }

    .position-share-extend {
        flex: 1 1 auto;
    }
}

@media (max-width: 575.98px) {
    .position-share-page {
        padding-right: 0.75rem;
        padding-left: 0.75rem;
    }

    .position-share-vehicle-card .card-body,
    .position-share-page-header {
        padding: 0.8rem;
    }

    .position-share-create {
        width: 100%;
    }

    .position-share-duration {
        min-width: 0;
        flex: 1;
    }

    .position-share-create-button {
        flex: 1;
    }

    .position-share-identity {
        flex-wrap: wrap;
    }

    .position-share-metadata {
        align-items: flex-start;
        flex-direction: column;
        gap: 0.15rem;
        white-space: normal;
    }

    .position-share-metadata-separator {
        display: none;
    }

    .position-share-metadata-old {
        margin-left: 1.2rem;
    }

    .position-share-actions {
        display: grid;
        grid-template-columns: 1fr 1fr;
        width: 100%;
    }

    .position-share-extend {
        display: grid;
        grid-column: 1 / -1;
        grid-template-columns: 1fr 1fr;
        width: 100%;
    }

    .position-share-extend-duration {
        width: 100%;
        min-width: 0;
    }

    .position-share-action {
        width: 100%;
    }
}
</style>